// shell/icons.jsx — Phase 0 shared inline-SVG icon module (btc design-match build)
//
// Extends the union of all icons needed by Board, Triggered, and Chat areas.
// These icons that already exist in heroVisuals/Icon.js are re-exported here
// via a unified ShellIcon component so shell code has ONE import path.
//
// DO NOT add lucide-react — it is not installed and must never be added.
// All paths are authored inline (stroke-2, round-cap/join, 24×24 viewBox).

import React from "react"

// ── Paths map ─────────────────────────────────────────────────────────────────
// Keys that also exist in heroVisuals/Icon.js are duplicated here for locality;
// shell code should import ShellIcon from this file, not from heroVisuals/Icon.js.

const PATHS = {
  // ── Already in Icon.js (kept here for shell locality) ─────────────────────
  "check":        <polyline points="20 6 9 17 4 12"/>,
  "target":       <><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></>,
  "trending-up":  <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
  "trendUp":      <><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></>,
  "pen":          <><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></>,
  "chart":        <><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/><line x1="3" y1="20" x2="21" y2="20"/></>,
  "ticket":       <><path d="M3 9a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4z"/><line x1="13" y1="7" x2="13" y2="17"/></>,
  "calendar":     <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
  "users":        <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
  "zap":          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>,
  "clock":        <><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>,
  // ── Phase 0 additions (NOT in Icon.js today) ──────────────────────────────
  // 4-point sparkle (true single star — distinct from Icon.js "sparkles" which is 2-spark)
  "sparkle":      <path d="M12 2l2.4 7.4L22 12l-7.6 2.6L12 22l-2.4-7.4L2 12l7.6-2.6z"/>,
  // chevrons
  "chevron-right": <polyline points="9 18 15 12 9 6"/>,
  "chevron-left":  <polyline points="15 18 9 12 15 6"/>,
  "chevron-down":  <polyline points="6 9 12 15 18 9"/>,
  "chevron-up":    <polyline points="18 15 12 9 6 15"/>,
  // actions
  "plus":         <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
  "x":            <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>,
  "arrow-up":     <><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></>,
  "arrow-left":   <><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></>,
  // dev/system
  "terminal":     <><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></>,
  // triggered-specific
  "megaphone":    <><path d="M3 11v2M21 12a9 9 0 0 1-9 9"/><path d="M11.5 3.9A9 9 0 0 1 21 12"/><path d="M3 13H1a1 1 0 0 1-1-1v0a1 1 0 0 1 1-1h2"/><path d="M5 13l4 7"/><path d="M21 5l-9 7 9 7"/></>,
  "bell":         <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></>,
  "sliders":      <><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></>,
  "shield":       <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>,
  "alert":        <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
  "dollar":       <><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></>,
  // ── Builder ICON_CHOICES + back-arrow additions (m3-16b create flow) ───────
  "repeat":       <><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></>,
  "send":         <><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>,
  "eye":          <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>,
}

// ── ShellIcon component ───────────────────────────────────────────────────────
// Usage: <ShellIcon name="chevron-right" size={16} />
// - name: string key from PATHS above
// - size: px (default 20); for stroke-only icons this matches both width and height
// - stroke: stroke-width (default 2); set to 1.5 for lighter appearance if needed
// - className: optional CSS class forwarded to the <svg>

const ShellIcon = ({ name, size = 20, stroke = 2, className, style }) => {
  const content = PATHS[name]
  if (!content) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[ShellIcon] unknown icon: "${name}"`)
    }
    return null
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
      style={style}
    >
      {content}
    </svg>
  )
}

export default ShellIcon
export { PATHS }
